import { MoreHorizontal, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface QuickAction {
  key: string
  label: string
  icon: LucideIcon
  onClick: () => void
  disabled?: boolean
  /** Shown as a tooltip: why it is disabled, or what it does. */
  hint?: string
  /** Only on a phone (CSS, no JS viewport check): the desktop has the same entry in the Meer menu. */
  mobileOnly?: boolean
}

export interface QuickMenuItem {
  key: string
  label: string
  onSelect: () => void
  destructive?: boolean
  /** Only from 768 px up (CSS): on a phone the same entry is a quick action. */
  desktopOnly?: boolean
}

// The label sets the width, so a longer one ("Contactpersoon") widens its button instead of
// running into the next one; min-w-11 keeps every button a 44 px target. A wider min-w
// would push six buttons (Notitie · E-mail · Gesprek · Meeting · Taak · Meer, about 270 px
// with these labels) onto two rows in the 360 px column, whose content is 294-318 px; on a
// phone (44 px circles, no side padding) they need 266 px of the 286 px a 360 px screen has.
const itemClass =
  'flex min-w-11 flex-col items-center gap-1 rounded-lg px-0.5 py-1.5 text-center text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground max-md:min-h-11 max-md:px-0'
const circleClass = 'grid size-9 place-items-center rounded-full border border-border bg-card max-md:size-11'
// No outline-none here: a muted fill is 1.2:1 on the popover, too faint as the only sign of
// keyboard focus (U1 took it off the other popover menus for the same reason).
const menuItemClass = 'rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted max-md:min-h-11'

/**
 * HubSpot's row of round icon buttons with the label under each (design §4.5, ED-19);
 * 44 px tap targets on a phone. "Meer" opens a menu with the rest.
 */
export function QuickActions({
  actions,
  more,
  moreLabel = 'Meer',
}: {
  actions: QuickAction[]
  more?: QuickMenuItem[]
  moreLabel?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    // `justify-between` hands the room that is left over to the gaps: one row like HubSpot's,
    // wrapping only in a column that is too narrow for it.
    <div role="group" aria-label="Snelacties" className="flex flex-wrap justify-between gap-y-1">
      {actions.map(({ key, label, icon: Icon, onClick, disabled, hint, mobileOnly }) => (
        <button key={key} type="button" className={cn(itemClass, mobileOnly && 'md:hidden')} onClick={onClick} disabled={disabled} title={hint}>
          <span className={circleClass}>
            <Icon className="size-4" aria-hidden />
          </span>
          {label}
        </button>
      ))}
      {more && more.length > 0 ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className={itemClass}>
              <span className={circleClass}>
                <MoreHorizontal className="size-4" aria-hidden />
              </span>
              {moreLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex w-56 flex-col p-1">
            {more.map((item) => (
              <button
                key={item.key}
                type="button"
                className={cn(menuItemClass, item.destructive && 'text-destructive', item.desktopOnly && 'max-md:hidden')}
                onClick={() => {
                  setOpen(false)
                  item.onSelect()
                }}
              >
                {item.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  )
}
